import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetAllClientMarketItchConnectionsQueryResponse, GetAllClientMarketItchConnectionsPathParams, GetAllClientMarketItchConnectionsQueryParams } from "../types/GetAllClientMarketItchConnections";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetAllClientMarketItchConnectionsClient = typeof client<GetAllClientMarketItchConnectionsQueryResponse, never, never>;
type GetAllClientMarketItchConnections = {
    data: GetAllClientMarketItchConnectionsQueryResponse;
    error: never;
    request: never;
    pathParams: GetAllClientMarketItchConnectionsPathParams;
    queryParams: GetAllClientMarketItchConnectionsQueryParams;
    headerParams: never;
    response: GetAllClientMarketItchConnectionsQueryResponse;
    client: {
        parameters: Partial<Parameters<GetAllClientMarketItchConnectionsClient>[0]>;
        return: Awaited<ReturnType<GetAllClientMarketItchConnectionsClient>>;
    };
};
export const getAllClientMarketItchConnectionsQueryKey = (clientCode: GetAllClientMarketItchConnectionsPathParams["clientCode"], marketCode: GetAllClientMarketItchConnectionsPathParams["marketCode"], params?: GetAllClientMarketItchConnections["queryParams"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/connections/itch", params: { clientCode: clientCode, marketCode: marketCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientMarketItchConnectionsQueryKey = ReturnType<typeof getAllClientMarketItchConnectionsQueryKey>;
export function getAllClientMarketItchConnectionsQueryOptions(clientCode: GetAllClientMarketItchConnectionsPathParams["clientCode"], marketCode: GetAllClientMarketItchConnectionsPathParams["marketCode"], params?: GetAllClientMarketItchConnections["queryParams"], options: GetAllClientMarketItchConnections["client"]["parameters"] = {}) {
    const queryKey = getAllClientMarketItchConnectionsQueryKey(clientCode, marketCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientMarketItchConnections["data"], GetAllClientMarketItchConnections["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/connections/itch`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a list of all ITCH connections for a clients market
 * @summary Get all ITCH connections
 * @link /api/clients/:clientCode/markets/:marketCode/connections/itch
 */
export function useGetAllClientMarketItchConnections<TData = GetAllClientMarketItchConnections["response"], TQueryData = GetAllClientMarketItchConnections["response"], TQueryKey extends QueryKey = GetAllClientMarketItchConnectionsQueryKey>(clientCode: GetAllClientMarketItchConnectionsPathParams["clientCode"], marketCode: GetAllClientMarketItchConnectionsPathParams["marketCode"], params?: GetAllClientMarketItchConnections["queryParams"], options: {
    query?: Partial<QueryObserverOptions<GetAllClientMarketItchConnections["response"], GetAllClientMarketItchConnections["error"], TData, TQueryData, TQueryKey>>;
    client?: GetAllClientMarketItchConnections["client"]["parameters"];
} = {}): UseQueryResult<TData, GetAllClientMarketItchConnections["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientMarketItchConnectionsQueryKey(clientCode, marketCode, params);
    const query = useQuery({
        ...getAllClientMarketItchConnectionsQueryOptions(clientCode, marketCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetAllClientMarketItchConnections["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getAllClientMarketItchConnectionsSuspenseQueryKey = (clientCode: GetAllClientMarketItchConnectionsPathParams["clientCode"], marketCode: GetAllClientMarketItchConnectionsPathParams["marketCode"], params?: GetAllClientMarketItchConnections["queryParams"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/connections/itch", params: { clientCode: clientCode, marketCode: marketCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientMarketItchConnectionsSuspenseQueryKey = ReturnType<typeof getAllClientMarketItchConnectionsSuspenseQueryKey>;
export function getAllClientMarketItchConnectionsSuspenseQueryOptions(clientCode: GetAllClientMarketItchConnectionsPathParams["clientCode"], marketCode: GetAllClientMarketItchConnectionsPathParams["marketCode"], params?: GetAllClientMarketItchConnections["queryParams"], options: GetAllClientMarketItchConnections["client"]["parameters"] = {}) {
    const queryKey = getAllClientMarketItchConnectionsSuspenseQueryKey(clientCode, marketCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientMarketItchConnections["data"], GetAllClientMarketItchConnections["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/connections/itch`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a list of all ITCH connections for a clients market
 * @summary Get all ITCH connections
 * @link /api/clients/:clientCode/markets/:marketCode/connections/itch
 */
export function useGetAllClientMarketItchConnectionsSuspense<TData = GetAllClientMarketItchConnections["response"], TQueryKey extends QueryKey = GetAllClientMarketItchConnectionsSuspenseQueryKey>(clientCode: GetAllClientMarketItchConnectionsPathParams["clientCode"], marketCode: GetAllClientMarketItchConnectionsPathParams["marketCode"], params?: GetAllClientMarketItchConnections["queryParams"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetAllClientMarketItchConnections["response"], GetAllClientMarketItchConnections["error"], TData, TQueryKey>>;
    client?: GetAllClientMarketItchConnections["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetAllClientMarketItchConnections["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientMarketItchConnectionsSuspenseQueryKey(clientCode, marketCode, params);
    const query = useSuspenseQuery({
        ...getAllClientMarketItchConnectionsSuspenseQueryOptions(clientCode, marketCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetAllClientMarketItchConnections["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}